import React, { FC, useEffect, useRef, useState } from 'react';

import { Heading, VStack } from '@chakra-ui/layout';
import {
    Alert,
    Box,
    Button,
    Center,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Spinner
} from '@chakra-ui/react';
import { intervalToDuration } from 'date-fns';
import { useRouter } from 'next/router';
import { FaRedoAlt } from 'react-icons/fa';
import { FormattedMessage } from 'react-intl';

import { EventType } from '~/components/Event';
import { useMicrositeConfig } from '~/components/Site/provider/MicrositeConfigProvider';
import SupportForm from '~/components/Support/SupportForm';
import { ValueFunctionPropsType } from '~/lib/localization/intl';

interface ISupportModal {
    isOpen: boolean;
    onClose: () => void;
}

const getFomfFaqUrl = (event: EventType) => {
    switch (event.country) {
        case 'AT':
            return 'https://www.fomf.at/faq';

        case 'DE':
            return 'https://www.fomf.de/faq';

        case 'CH':
            return 'https://www.fomf.ch/faq';

        case 'UK':
            return 'https://www.cmed.org.uk/faq';
    }

    return '';
};

const SupportQuickFixTip: FC<{
    reloadPage: () => void;
    initialFocusRef: React.RefObject<HTMLButtonElement>;
}> = ({ reloadPage, initialFocusRef }) => {
    const { event, site } = useMicrositeConfig();

    const faqUrl = event && site.name === 'FomF Event Site' ? getFomfFaqUrl(event) : '';

    return (
        <Box mb="20px">
            <Alert status="info" borderRadius="md" textAlign="center">
                <Box w="full">
                    <VStack spacing="4" w="full" alignItems="stretch">
                        <Box>
                            <FormattedMessage
                                id="support.problems_header"
                                defaultMessage="Haben Sie Probleme im Livestream?"
                            />
                        </Box>
                        <Box>
                            <Button
                                ref={initialFocusRef}
                                onClick={() => {
                                    reloadPage();
                                }}
                                size="md"
                                variant="solid"
                                colorScheme="blue"
                                whiteSpace="normal"
                                height="auto"
                                width="auto"
                                py="7px"
                                px="20px"
                                rightIcon={<FaRedoAlt />}
                            >
                                <FormattedMessage
                                    id="support.button.page_reload"
                                    defaultMessage="Seite neu laden"
                                />
                            </Button>
                        </Box>
                        {faqUrl ? (
                            <Box>
                                <FormattedMessage
                                    id="support.faq.text"
                                    defaultMessage="Nutzen Sie unsere <link_faq>FAQ</link_faq>"
                                    values={{
                                        link_faq: (...chunks: ValueFunctionPropsType) => (
                                            <Link
                                                isExternal={true}
                                                href={faqUrl}
                                                textDecoration="underline"
                                            >
                                                {chunks}
                                            </Link>
                                        )
                                    }}
                                />
                            </Box>
                        ) : null}
                    </VStack>
                </Box>
            </Alert>
        </Box>
    );
};

const SupportModal: FC<ISupportModal> = ({ isOpen = false, onClose, ...props }) => {
    const initialFocusRef = useRef<HTMLButtonElement>(null);
    const router = useRouter();
    const [pageIsReloading, setPageIsReloading] = useState(false);
    const [showSupportForm, setShowSupportForm] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        const quickfixDoneAt = window.sessionStorage.getItem('support-quickfix-done');

        if (quickfixDoneAt) {
            const interval = intervalToDuration({
                start: new Date(quickfixDoneAt),
                end: new Date()
            });

            if (interval && (interval.minutes || 1) > 240) {
                reloadPage();
            } else {
                setShowSupportForm(false);
            }
        } else {
            reloadPage();
        }
    }, [isOpen]);

    const reloadPage = () => {
        window.sessionStorage.setItem('support-quickfix-done', new Date().toUTCString());
        router.reload();
        setPageIsReloading(true);
    };

    return (
        <Modal
            initialFocusRef={initialFocusRef}
            isOpen={isOpen}
            onClose={onClose}
            size="2xl"
            {...props}
        >
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    <FormattedMessage id="support.modal.header" defaultMessage="Support-Anfrage" />
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    {pageIsReloading ? (
                        <Center py="40px">
                            <Spinner size="xl" />
                        </Center>
                    ) : (
                        <>
                            {showSupportForm ? (
                                <SupportForm />
                            ) : (
                                <VStack spacing="8" py="8" textAlign="center">
                                    <SupportQuickFixTip
                                        initialFocusRef={initialFocusRef}
                                        reloadPage={reloadPage}
                                    />
                                    <Heading size="sm" color="gray.600">
                                        <FormattedMessage
                                            id="support.modal.show_contact_form"
                                            defaultMessage="Seite neuladen sowie FAQs haben nicht geholfen?"
                                        />
                                    </Heading>

                                    <Button
                                        onClick={() => setShowSupportForm(true)}
                                        colorScheme="blue"
                                    >
                                        <FormattedMessage
                                            id="support.modal.show_contact_form_button"
                                            defaultMessage="Senden Sie uns eine Nachricht"
                                        />
                                    </Button>
                                </VStack>
                            )}
                        </>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default SupportModal;
